/** Computer status in Settings, with workspace entry points to Performance. */
import { useCallback, useEffect, useRef, useState } from 'react';
// standards: allow file-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
import {
  computerCheckMessage,
  computerFeature,
  customerHealthReport,
  speedBoostHardwareNote,
  speedBoostRecheckSummary,
  speedBoostState,
} from './customer-health-report';
import { TooltipButton } from './TooltipButton';
import { ComputerFeatureArt } from './PerformanceIllustrations';
import { ExtractionSettings } from './ExtractionSettings';
import { GraphicsCardTiles, useGraphicsCards } from './GraphicsCards';
import { speedBoostStatus } from './SpeedBoostStatus';
import { shortDateTime } from './short-date-time';
import {
  CircleCheck,
  LoaderCircle,
  Info,
  CircleDashed,
  RotateCw,
  Settings2,
  TriangleAlert,
  Zap,
  ZapOff,
} from 'lucide-react';

import type {
  AcceleratorReport,
  SpeedBoostRecheck,
  WorkstationHealthReport,
} from '../shared/desktop-api';

const desktop = () => window.capturePointDesktop;

/** A real computer check: measure the card now, retry the speed boost, report what was found. */
function useSpeedBoostRecheck() {
  const [recheck, setRecheck] = useState<SpeedBoostRecheck | null>(null);
  const [running, setRunning] = useState(false);
  const [failed, setFailed] = useState(false);
  const live = useRef(true);
  useEffect(() => {
    live.current = true;
    return () => {
      live.current = false;
    };
  }, []);
  const run = useCallback(async () => {
    const api = desktop();
    if (!api?.retrySpeedBoost || running) return;
    setRunning(true);
    setFailed(false);
    try {
      const next = await api.retrySpeedBoost();
      if (live.current) setRecheck(next);
    } catch {
      if (live.current) setFailed(true);
    } finally {
      if (live.current) setRunning(false);
    }
  }, [running]);
  return { recheck, running, failed, run, available: Boolean(desktop()?.retrySpeedBoost) };
}

/** The lines a computer check found, with Try again while the speed boost is not on. */
function SpeedBoostRecheckResult({
  recheck,
  running,
  failed,
  onRetry,
}: {
  recheck: SpeedBoostRecheck | null;
  running: boolean;
  failed: boolean;
  onRetry: () => void;
}) {
  if (!recheck && !failed) return null;
  const summary = recheck ? speedBoostRecheckSummary(recheck) : null;
  return (
    <div className={`speed-boost-recheck ${summary?.tone ?? 'attention'}`} role="status">
      <strong>{summary ? summary.title : 'The computer check could not be completed'}</strong>
      {summary && (
        <ul>
          {summary.lines.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      )}
      {(failed || summary?.canRetry) && (
        <button
          type="button"
          className="secondary-button small"
          disabled={running}
          onClick={onRetry}
        >
          {running ? <LoaderCircle className="spin" size={14} /> : <RotateCw size={14} />}{' '}
          {running ? 'Checking…' : 'Try again'}
        </button>
      )}
    </div>
  );
}

/**
 * The computer status panel on Settings > Performance.
 */
export function WorkstationHealthPanel({ embedded = false }: { embedded?: boolean }) {
  const [report, setReport] = useState<WorkstationHealthReport | null>(null);
  const [checking, setChecking] = useState(true);
  const [failure, setFailure] = useState<string | null>(null);
  const live = useRef(false);
  const reportRevision = useRef(0);

  const load = useCallback(async (refresh: boolean) => {
    const request = ++reportRevision.current;
    const api = desktop();
    if (!api) {
      setChecking(false);
      setFailure('The computer check is unavailable in this view.');
      return;
    }
    setChecking(true);
    setFailure(null);
    try {
      const next = await api.getWorkstationHealth(refresh);
      if (live.current && reportRevision.current === request) setReport(customerHealthReport(next));
    } catch {
      if (live.current && reportRevision.current === request)
        setFailure(
          'The computer check could not be completed. Try again, or contact support if this continues.',
        );
    } finally {
      if (live.current && reportRevision.current === request) setChecking(false);
    }
  }, []);

  useEffect(() => {
    live.current = true;
    // The main process re-checks at start-up and again after the first batch,
    // and pushes what it finds. Taking that push means this screen is current
    // when it opens rather than showing whatever was true when it last loaded.
    const stop = desktop()?.onWorkstationHealthChanged((next) => {
      if (!live.current) return;
      reportRevision.current += 1;
      setReport(customerHealthReport(next));
      setFailure(null);
      setChecking(false);
    });
    void load(true);
    return () => {
      live.current = false;
      reportRevision.current += 1;
      stop?.();
    };
  }, [load]);

  const recheck = useSpeedBoostRecheck();
  const checkNow = () => {
    if (recheck.available) void recheck.run();
    else void load(true);
  };
  const message = computerCheckMessage(report);
  const graphicsCards = useGraphicsCards();
  // Every card turned off is the processor only: Speed boost says Off from the choice, not
  // from an engine that has not restarted yet.
  const boost = graphicsCards.processorOnly
    ? { mode: 'off' as const, label: 'Off' }
    : speedBoostStatus(report);

  return (
    <section className={`card workstation-health visual-computer-check ${message.tone}`}>
      {!embedded && (
        <header className="workstation-health-head">
          <h2>
            <Zap size={18} /> Computer check
          </h2>
        </header>
      )}

      {/* Never a bare spinner. The check waits on the reading runtime, and an
          operator watching it needs to know that is what it is waiting for. */}
      {checking && !report && (
        <p className="workstation-health-waiting">
          <LoaderCircle className="spin" size={13} />
          Checking whether Paige is ready and its speed boost is working…
        </p>
      )}

      {failure && (
        <p
          className="workstation-health-failure"
          role="alert"
          data-telemetry="error:computer-check-failed"
        >
          {report && <strong>Could not refresh. Showing the last received report. </strong>}
          {failure}
          <button
            type="button"
            className="quiet-button small"
            disabled={checking}
            onClick={() => void load(true)}
          >
            Try again
          </button>
        </p>
      )}

      {report && (
        <>
          <section className="speed-boost-section" aria-label="Speed boost">
            <div className="boost-overview">
              <strong className="boost-heading">
                <Zap size={20} aria-hidden="true" />
                Speed boost
              </strong>
              <TooltipButton
                type="button"
                className="boost-info icon-button"
                aria-label="About speed boost"
                tooltip={speedBoostHardwareNote}
                helpOnClick
              >
                <Info size={16} />
              </TooltipButton>
              <span className={`boost-summary ${boost.mode}`} role="status">
                {boost.label}
              </span>
              <TooltipButton
                type="button"
                className="boost-refresh icon-button"
                aria-label="Check speed boost again"
                tooltip="Check again"
                onClick={checkNow}
                disabled={checking || recheck.running}
              >
                {checking || recheck.running ? (
                  <LoaderCircle className="spin" size={16} />
                ) : (
                  <RotateCw size={16} />
                )}
              </TooltipButton>
              <div className="boost-machine-info">
                <small className="boost-checked">
                  Last checked {shortDateTime(report.checkedAt)}
                </small>
              </div>
              {/* Cards the setting cannot name (no NVIDIA driver tool: an AMD or Intel card) keep the plain line. */}
              {!graphicsCards.view?.cards.length &&
                !graphicsCards.error &&
                report.graphicsCards &&
                report.graphicsCards.length > 0 && (
                  <small className="boost-card-names">{report.graphicsCards.join(' · ')}</small>
                )}
            </div>
            <GraphicsCardTiles state={graphicsCards} />
            <div className="computer-features boost-features" aria-label="Speed boost results">
              {report.components
                .filter(
                  (component) =>
                    component.component === 'extraction' || component.component === 'ocr',
                )
                .map((component) => (
                  <FeatureCard
                    key={component.component}
                    component={component}
                    processorOnly={graphicsCards.processorOnly}
                  />
                ))}
            </div>
            {message.tone === 'attention' && (
              <div className="workstation-health-outcome attention" role="status">
                <strong>{message.title}</strong>
                <p>{message.action ?? message.detail}</p>
                {Boolean(report.modelReachability?.unreachableDocuments) && <p>{message.detail}</p>}
                {recheck.available && !recheck.recheck && !recheck.failed && (
                  <button
                    type="button"
                    className="secondary-button small"
                    disabled={recheck.running}
                    onClick={() => void recheck.run()}
                  >
                    {recheck.running ? (
                      <LoaderCircle className="spin" size={14} />
                    ) : (
                      <RotateCw size={14} />
                    )}{' '}
                    {recheck.running ? 'Checking…' : 'Try again'}
                  </button>
                )}
              </div>
            )}
            <SpeedBoostRecheckResult
              recheck={recheck.recheck}
              running={recheck.running}
              failed={recheck.failed}
              onRetry={() => void recheck.run()}
            />
          </section>
        </>
      )}
      {(report || embedded) && (
        <section className="processing-section" aria-label="Processing">
          <h2 className="processing-heading">
            <Settings2 size={20} aria-hidden="true" />
            Processing
          </h2>
          <div
            className={`computer-features processing-features${embedded ? ' with-mode' : ''}`}
            aria-label="Processing speeds and preferences"
          >
            {report?.components
              .filter(
                (component) =>
                  component.component !== 'extraction' && component.component !== 'ocr',
              )
              .map((component) => (
                <FeatureCard key={component.component} component={component} showStatus={false} />
              ))}
            {embedded && <ExtractionSettings />}
          </div>
        </section>
      )}
    </section>
  );
}

function FeatureCard({
  component,
  showStatus = true,
  processorOnly = false,
}: {
  component: AcceleratorReport;
  showStatus?: boolean;
  processorOnly?: boolean;
}) {
  // Every graphics card turned off: this work is on the processor from the next document, whatever the last check saw.
  const feature = processorOnly
    ? { ...computerFeature(component), label: 'Speed boost off', tone: 'neutral', note: null }
    : computerFeature(component);
  const label = feature.label === 'Speed boost not checked yet' ? 'Not checked yet' : feature.label;
  // The person's own work: the last completed batch, never the speed test (which has its score).
  const batch = component.lastMeasuredRate;
  const rate =
    batch && Number.isFinite(batch.pagesPerMinute) && batch.pagesPerMinute >= 0 ? batch : null;
  return (
    <article
      className={`computer-feature ${feature.tone}${!processorOnly && speedBoostState(component) === 'on' ? ' boosted' : ''}`}
    >
      <ComputerFeatureArt kind={component.component} />
      <strong>{feature.title}</strong>
      <p>{feature.description}</p>
      <div className="computer-feature-rate">
        <strong>
          {rate
            ? rate.pagesPerMinute.toLocaleString(undefined, {
                maximumFractionDigits: 1,
                minimumFractionDigits: 1,
              })
            : '—'}
        </strong>
        <small>{rate ? 'pages / min · your last batch' : 'No speed recorded'}</small>
        {/* When it was gathered: a batch from days ago and the speed test beside it are different evidence. */}
        {rate?.measuredAt && (
          <small className="computer-feature-when">{shortDateTime(rate.measuredAt)}</small>
        )}
      </div>
      {showStatus && (
        <span className={`computer-feature-status ${feature.tone}`}>
          {feature.tone === 'attention' ? (
            <TriangleAlert size={14} />
          ) : feature.label === 'Speed boost on' ? (
            <Zap size={14} />
          ) : feature.label === 'Speed boost off' ? (
            <ZapOff size={14} />
          ) : feature.tone === 'ready' ? (
            <CircleCheck size={14} />
          ) : (
            <CircleDashed size={14} />
          )}
          {label}
        </span>
      )}
      {/* A problem says what to do; a deliberate "off" says why, so On above it reads as true. */}
      {feature.note &&
        (feature.tone === 'attention' ||
          (!processorOnly && speedBoostState(component) === 'off')) && (
          <small>{feature.note}</small>
        )}
    </article>
  );
}
